<div class="waiver-sign">

  <!-- Loading -->
  <div *ngIf="loading" class="waiver-sign__loading">
    <i class="fas fa-spinner fa-spin"></i>
    <p>Loading waiver...</p>
  </div>

  <!-- Not Found -->
  <div *ngIf="!loading && notFound" class="waiver-sign__not-found">
    <i class="fas fa-exclamation-triangle"></i>
    <h3>Waiver Not Found</h3>
    <p>{{ loadError || 'The waiver you\'re looking for doesn\'t exist or the link may have expired.' }}</p>
  </div>

  <!-- Waiver Form -->
  <div *ngIf="!loading && !notFound && waiver" class="waiver-sign__card">

    <!-- Envelope navigation — only when this waiver was opened from an
         envelope. Lets the signer step back to the checklist or jump to
         a sibling item without submitting. -->
    <div *ngIf="isEnvelopeContext && envelopeItems.length > 0" class="waiver-sign__envelope-nav">
      <button type="button" class="btn btn-sm btn-outline-secondary"
              (click)="returnToEnvelope()"
              title="Return to the list of waivers in this envelope">
        <i class="fas fa-list-ul"></i>
        <span>Back to Envelope</span>
      </button>

      <div class="waiver-sign__envelope-nav-position" *ngIf="currentItemIndex >= 0">
        Item {{ currentItemIndex + 1 }} of {{ envelopeItems.length }}
      </div>

      <div class="waiver-sign__envelope-nav-controls">
        <button type="button" class="btn btn-sm btn-outline-primary"
                (click)="goToPrev()"
                [disabled]="!prevPendingItem"
                title="Previous pending waiver in this envelope">
          <i class="fas fa-chevron-left"></i>
          <span>Prev</span>
        </button>
        <button type="button" class="btn btn-sm btn-outline-primary"
                (click)="goToNext()"
                [disabled]="!nextPendingItem"
                title="Next pending waiver in this envelope">
          <span>Next</span>
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Header -->
    <div class="waiver-sign__header">
      <h2>{{ waiver.name }}</h2>
      <div class="waiver-sign__version">
        <i class="fas fa-code-branch"></i> {{ waiver.versionLabel }}
      </div>
    </div>

    <!-- Submitted Confirmation -->
    <div *ngIf="submitted" class="waiver-sign__success">
      <div class="waiver-sign__success-icon">
        <i class="fas fa-check-circle"></i>
      </div>
      <h3>Waiver Submitted Successfully</h3>
      <p *ngIf="!isEnvelopeContext">Thank you for completing the waiver. You may close this page.</p>
      <p *ngIf="isEnvelopeContext">Thank you. Return to your envelope to complete any remaining documents.</p>
      <button *ngIf="isEnvelopeContext"
              class="btn btn-primary btn-sm"
              (click)="returnToEnvelope()">
        <i class="fas fa-arrow-left"></i> Back to Envelope
      </button>
      <button *ngIf="!isEnvelopeContext"
              class="btn btn-outline-primary btn-sm"
              (click)="resetForm()">
        <i class="fas fa-redo"></i> Submit Another
      </button>
    </div>

    <!-- Attached template PDF, rendered above the form so the signer sees
         the legal text before filling out identity + signature fields. -->
    <div *ngIf="!submitted && pdfSafeUrl" class="waiver-sign__pdf">
      <iframe [src]="pdfSafeUrl" class="waiver-sign__pdf-frame" title="Waiver PDF"></iframe>
      <p class="waiver-sign__pdf-agreement">
        <em>By signing below you understand and agree to this content above.</em>
      </p>
    </div>

    <!-- Form Body -->
    <form *ngIf="!submitted" #signForm="ngForm" (ngSubmit)="submitWaiver()" class="waiver-sign__body">

      <div *ngFor="let field of waiver.fields"
           class="waiver-sign__field"
           [class.waiver-sign__field--invalid]="invalidFieldIds.has(field.id)">

        <!-- Text Body -->
        <ng-container *ngIf="field.type === 'textBody'">
          <div class="waiver-sign__text-body">{{ field.value || '' }}</div>
        </ng-container>

        <!-- Name -->
        <ng-container *ngIf="field.type === 'name'">
          <label [for]="field.id">
            {{ field.label }}
            <span class="text-danger" *ngIf="field.required">*</span>
          </label>
          <input
            type="text"
            class="form-control"
            [id]="field.id"
            [placeholder]="field.placeholder"
            [(ngModel)]="formData[field.id]"
            [name]="field.id"
            [required]="field.required" />
        </ng-container>

        <!-- Email -->
        <ng-container *ngIf="field.type === 'email'">
          <label [for]="field.id">
            {{ field.label }}
            <span class="text-danger" *ngIf="field.required">*</span>
          </label>
          <input
            #emailInput="ngModel"
            type="email"
            class="form-control"
            [id]="field.id"
            [placeholder]="field.placeholder"
            [(ngModel)]="formData[field.id]"
            [name]="field.id"
            [required]="field.required" />
          <!-- Gated the same way as the phone hint — wait for blur or a
               submit attempt so we don't nag mid-typing. -->
          <small class="text-danger"
                 *ngIf="(emailInput.touched || signForm.submitted) && isEmailFormatInvalid(field.id)">
            Enter a valid email address (e.g. name&#64;example.com).
          </small>
        </ng-container>

        <!-- Phone — always optional at signing regardless of the builder's
             `required` flag. Format validation still runs when a value is
             entered so downstream envelope routing gets a usable number. -->
        <ng-container *ngIf="field.type === 'phone'">
          <label [for]="field.id">
            {{ field.label }}
          </label>
          <input
            #phoneInput="ngModel"
            phoneFormat
            type="tel"
            class="form-control"
            [id]="field.id"
            [placeholder]="field.placeholder"
            [(ngModel)]="formData[field.id]"
            [name]="field.id" />
          <!-- Gate on `touched` so the hint doesn't nag mid-typing (e.g. a
               user typing "5" would otherwise see the error flash before
               finishing a 10-digit number). Also revealed by a submit
               attempt via the form's `submitted` state. -->
          <small class="text-danger"
                 *ngIf="(phoneInput.touched || signForm.submitted) && isPhoneFormatInvalid(field.id)">
            Enter a phone number with at least 10 digits.
          </small>
        </ng-container>

        <!-- The "Is Under N" widget used to render here. Retired: the
             participant's date of birth decides whether a guardian must
             sign, so asking the signer to declare it was redundant. Legacy
             waivers may still carry the field; it deliberately renders
             nothing rather than being an unanswerable leftover. -->

        <!-- Signature -->
        <ng-container *ngIf="field.type === 'signature'">
          <label>
            {{ field.label }}
            <span class="text-danger">*</span>
          </label>
          <div class="waiver-sign__signature-pad">
            <canvas
              appSignaturePad
              width="500"
              height="150"
              (signatureChange)="formData[field.id] = $event"
              #sigPad="signaturePad"></canvas>
            <button type="button" class="btn btn-sm btn-outline-secondary mt-1" (click)="sigPad.clear()">
              <i class="fas fa-eraser"></i> Clear
            </button>
          </div>
        </ng-container>

        <!-- Guardian Name -->
        <ng-container *ngIf="field.type === 'guardianName' && isGuardianVisible()">
          <label [for]="field.id">
            {{ field.label }}
            <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control"
            [id]="field.id"
            [placeholder]="field.placeholder"
            [(ngModel)]="formData[field.id]"
            [name]="field.id"
            required />
        </ng-container>

        <!-- Guardian Signature -->
        <ng-container *ngIf="field.type === 'guardianSignature' && isGuardianVisible()">
          <label>
            {{ field.label }}
            <span class="text-danger">*</span>
          </label>
          <div class="waiver-sign__signature-pad waiver-sign__signature-pad--guardian">
            <canvas
              appSignaturePad
              width="500"
              height="150"
              (signatureChange)="formData[field.id] = $event"
              #guardianSigPad="signaturePad"></canvas>
            <button type="button" class="btn btn-sm btn-outline-secondary mt-1" (click)="guardianSigPad.clear()">
              <i class="fas fa-eraser"></i> Clear
            </button>
          </div>
        </ng-container>

      </div>

      <!-- Validation Error -->
      <div *ngIf="validationError" class="waiver-sign__validation-error">
        <i class="fas fa-exclamation-circle"></i>
        {{ validationError }}
      </div>

      <!-- Submit-time server error. Rendered instead of the success
           confirmation when the network write failed, so the customer
           doesn't mistake a silent failure for a completed signature.
           The form stays editable so a retry submits the current values
           without re-typing. -->
      <div *ngIf="submitError" class="waiver-sign__validation-error">
        <i class="fas fa-exclamation-triangle"></i>
        {{ submitError }}
      </div>

      <!-- Submit -->
      <div class="waiver-sign__actions">
        <button
          type="submit"
          class="btn btn-primary"
          [disabled]="submitting">
          <i class="fas" [ngClass]="submitting ? 'fa-spinner fa-spin' : (submitError ? 'fa-redo' : 'fa-check')"></i>
          {{ submitting ? 'Submitting...' : (submitError ? 'Try Again' : 'Submit Waiver') }}
        </button>
      </div>
    </form>

    <!-- Footer -->
    <div class="waiver-sign__footer">
      <small>&copy; {{ currentYear }} Fleet Adventures. All rights reserved.</small>
    </div>
  </div>
</div>
